import { useState } from 'react';
import { getBackendDetails } from '@/utils/getBackendDetails';
import { User } from '@/generated/graphql';
import { useJWT } from './useAuth';

interface LoginResponse {
  success: boolean;
  user: User | null;
  accessToken: string;
}

export function useLogin() {
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { storeAccessToken } = useJWT();

  const login = async (email: string, password: string) => {
    setIsLoading(true);
    setError(null);

    const { hostname, httpProtocol } = getBackendDetails();
    const loginUrl = `${httpProtocol}://${hostname}/auth/login`;

    try {
      const response = await fetch(loginUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          password,
        }),
        credentials: 'include', // Important for cookies
      });

      const data: LoginResponse = await response.json();

      if (!response.ok) {
        throw new Error(data.user ? 'Invalid credentials' : 'Login failed');
      }

      // Store the access token
      if (data.success && data.accessToken) {
        storeAccessToken(data.accessToken);
      }

      return data;
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'An error occurred during login',
      );
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    login,
    error,
    isLoading,
  };
}
